<script lang="ts">
  import {DraftService, ThumbDraftService} from '@/services';
  import Dialog from '../../../components/dialog/Dialog.svelte';
  import {draftStore, project, preview, menu, thumbDraft, thumbnail} from '@/store';
  import Swal from 'sweetalert2';
  import {draftName, DraftState} from '@/store/drafts';
  import {MENU} from '@/types/common';
  import editorInstance from '@/lib/editor/editor';

  let service: DraftService | ThumbDraftService;
  let store: typeof draftStore | typeof thumbDraft;

  export let draft: DraftState;
  export let isSaveDialogOpen = false;
  export let projectName: string;
  export let isProject: boolean = false;
  export let isSavingProject = false;

  async function saveAsProject(clone = false) {
    isSavingProject = true;
    const result = {value: projectName};

    if (result.value == '') {
      return false;
    }

    if (typeof draft.currentCloudDraftId !== 'number' || draft.currentCloudDraftId <= 0) {
      return false;
    }

    if (clone) {
      let _project =
        $menu === MENU.CLIPS ? JSON.parse(JSON.stringify($project)) : JSON.parse(JSON.stringify($thumbnail));
      let _preview = JSON.parse(JSON.stringify($preview));
      try {
        delete _project.transcription;
        delete _project.video?.transcription;
      } catch (err) {
        console.warn('Could not delete transcriptions from index');
      }
      await store
        .createNewDraft(_project, _preview, projectName)
        .then(() => {
          Swal.fire({
            position: 'bottom-end',
            icon: 'success',
            title: 'Project Saved',
            showConfirmButton: false,
            timer: 1500,
            toast: true
          });
        })
        .catch(error => {
          console.warn(`Failed to Save Project: ${JSON.stringify(error)}`);
          Swal.fire({
            position: 'bottom-end',
            icon: 'error',
            title: 'Failed to Save Project',
            showConfirmButton: false,
            timer: 1500,
            toast: true
          });
        });
    } else {
      let _project =
        $menu === MENU.CLIPS ? JSON.parse(JSON.stringify($project)) : JSON.parse(JSON.stringify($thumbnail));
      let _preview = JSON.parse(JSON.stringify($preview));
      await thumbDraft.updateDraft(_project, projectName, _preview);
      await service
        .makeProject(draft.currentCloudDraftId as number, result.value)
        .then(() => {
          Swal.fire({
            position: 'bottom-end',
            icon: 'success',
            title: 'Project Saved',
            showConfirmButton: false,
            timer: 1500,
            toast: true
          });
          store.markDraftAsProject(projectName);
        })
        .catch(error => {
          console.warn(`Failed to Save Project: ${JSON.stringify(error)}`);
          Swal.fire({
            position: 'bottom-end',
            icon: 'error',
            title: 'Failed to Save Project',
            showConfirmButton: false,
            timer: 1500,
            toast: true
          });
        });
    }

    isSaveDialogOpen = false;
    isSavingProject = false;
  }

  async function saveChanges() {
    if (isProject) {
      isSavingProject = true;
      editorInstance.history.updateNode();
      let _project =
        $menu === MENU.CLIPS ? JSON.parse(JSON.stringify($project)) : JSON.parse(JSON.stringify($thumbnail));
      let _preview = JSON.parse(JSON.stringify($preview));

      try {
        delete _project.transcription;
        delete _project.video?.transcription;

        await store
          .updateDraft(_project, projectName, _preview)
          .then(() => {
            Swal.fire({
              position: 'bottom-end',
              icon: 'success',
              title: 'Changes Saved',
              showConfirmButton: false,
              timer: 1500,
              toast: true
            });
            isSavingProject = false;
          })
          .catch(error => {
            Swal.fire({
              position: 'bottom-end',
              icon: 'error',
              title: 'Saving Failed',
              showConfirmButton: false,
              timer: 1500,
              toast: true
            });
          });
      } catch (error) {
        console.warn(`Failed Saving Project`);
      }
      isSaveDialogOpen = false;
    }
  }

  $: {
    if ($menu === MENU.CLIPS) {
      service = new DraftService();
      store = draftStore;
    }

    if ($menu === MENU.THUMBNAILS) {
      service = new ThumbDraftService();
      store = thumbDraft;
    }
  }
</script>

<Dialog bind:isDialogOpen={isSaveDialogOpen} useIfShow={true}>
  <div class="pointer-events-none z-20 flex h-full w-full items-center justify-center">
    <div
      class="pointer-events-auto h-auto w-full min-w-[280px] rounded-2xl border border-zinc-400 bg-white p-12 shadow-lg dark:border-zinc-600 dark:bg-zinc-700 lg:max-w-[680px]"
    >
      <div class="flex items-center text-zinc-900 dark:text-zinc-200">
        <div class="flex items-center gap-3">
          <h1 class="text-2xl font-bold text-dark dark:text-dark">Save Project</h1>
        </div>
        <button on:click={() => (isSaveDialogOpen = false)} class="ml-auto rounded-full px-2 hover:bg-zinc-100">
          <i class="fa-solid fa-xmark p-2 text-lg text-black" aria-hidden="true"></i>
        </button>
      </div>
      <div class="grid py-6">
        <div class="py-2">
          <label for="name" class="block text-sm font-medium text-dark-400 dark:text-zinc-200">Project Name</label>
          <div class="flex pt-2">
            <input
              bind:value={projectName}
              name="name"
              type="text"
              placeholder=""
              class="block w-full rounded rounded-tl-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-gray-500 placeholder-gray-300 focus:border-primary focus:outline-none focus:ring focus:ring-primary focus:ring-opacity-40 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:placeholder-zinc-500 dark:focus:border-blue-300"
              required
            />
          </div>
        </div>
        <p class="pt-4 text-start text-sm font-medium text-dark-400">
          Note: Changes auto save every 5 seconds after your updates.
        </p>
      </div>
      <div class="flex justify-end gap-2">
        <button
          on:click={() => (isSaveDialogOpen = false)}
          class="{isSavingProject
            ? 'bg-zinc-100'
            : 'border border-2 border-zinc-400'} flex w-full items-center rounded-md px-3 py-2 font-medium text-zinc-400 lg:w-auto"
          disabled={isSavingProject}
        >
          <span>Cancel</span>
        </button>
        <button
          on:click={() => (isProject ? saveAsProject(true) : saveAsProject())}
          class="flex w-full items-center gap-1 rounded-md border border-2 border-rose-500 px-3 py-2 font-medium text-rose-500 hover:bg-rose-600 hover:text-zinc-100 lg:w-auto"
          disabled={isSavingProject}
        >
          <div
            class="{isSavingProject
              ? ''
              : 'hidden'} spinner h-4 w-4 !border-zinc-100 !border-t-rose-500 dark:!border-zinc-700 dark:!border-t-rose-500"
          ></div>
          <!-- <i class="{isSavingProject ? 'hidden' : ''} fa-solid fa-floppy-disk" /> -->
          <span>{isProject ? 'Save as New Draft' : 'Save Project'}</span>
        </button>
        {#if isProject}
          <button
            on:click={() => saveChanges()}
            class="flex w-full items-center gap-1 rounded-md bg-rose-500 px-3 py-2 font-medium text-zinc-100 hover:bg-rose-600 lg:w-auto"
            disabled={isSavingProject}
          >
            <div
              class="{isSavingProject
                ? ''
                : 'hidden'} spinner h-4 w-4 !border-zinc-100 !border-t-rose-500 dark:!border-zinc-700 dark:!border-t-rose-500"
            ></div>
            <i class="{isSavingProject ? 'hidden' : ''} fa-solid fa-floppy-disk" />
            <span>Save Changes</span>
          </button>
        {/if}
      </div>
    </div>
  </div>
</Dialog>
